import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { last } from 'lodash-es';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ValidationValueMessage } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { mapToUploaderName } from 'domain/user/User';
import { Namespace, ParseKeys } from 'i18next';
import { IInput } from 'library/components/controls/common/IInput';
import { ClientFileDisplay } from 'library/components/controls/files/ClientFileDisplay';
import { FileDragBox } from 'library/components/controls/files/FileDragBox';
import {
	ALLOWED_FILE_EXTENSIONS_LIST,
	convertFormatArrayToExtensionsValidationRegexp,
	convertFormatArrayToFileExtensionsString,
	isValidFileUploadDuplicates,
} from 'library/components/controls/files/fileUploadValidation';
import { FileWarningDialog } from 'library/hooks/UseUploadController/FilesWarningDialog';
import { useUploadController } from 'library/hooks/UseUploadController/UploadHook';
import { selectUser } from 'library/redux/application';
import {
	FileData,
	FileDeletedPayload,
	FileUploadState,
	mapFileToFileData,
	selectAttachmentFolderName,
	selectFileUploaded,
} from 'library/redux/fileUpload';
import { useAppDispatch, useAppSelector } from 'library/redux/hooks';
import { RootState } from 'store';

/**
 * Redux action creators and selectors for managing file upload state.
 */
export interface IFileUploadStateHandlers {
	/** Action creator called when an attachment folder is created on the server. */
	attachmentFolderCreated: ActionCreatorWithPayload<string, string>;
	/** Action creator called when a file is deleted. */
	fileDeleted: ActionCreatorWithPayload<FileDeletedPayload, string>;
	/** Action creator called when a file is successfully uploaded. */
	fileUploaded: ActionCreatorWithPayload<FileData, string>;
	/** Selector function to retrieve the file upload state from Redux store. */
	fileStateSelector: (s: RootState) => FileUploadState;
}

/**
 * Props for the UploadFileSection component.
 * @extends IInput
 */
export interface IUploadFileSectionProps extends IInput {
	/** Translation key for the descriptive text shown below the title. @default 'fileUpload:upload-files-text' */
	text?: ParseKeys<Namespace>;
	/** Translation key for the section title. @default 'fileUpload:upload-files-title' */
	title?: ParseKeys<Namespace>;
	/** Whether to display the section with an error border. @default false */
	isError?: boolean;
	/** Whether to validate files before adding them to the upload queue. @default false */
	isValidationOnUpload?: boolean;
	/** Redux action creators and selectors for managing file upload state. */
	fileUploadStateHandlers: IFileUploadStateHandlers;
	/** Callback that registers a validation handler function for form validation. */
	validate: (handler: () => boolean) => void;
	/** Validation rules for minimum and maximum file count with error messages. */
	validation?: {
		/** Minimum number of files required. */
		min?: ValidationValueMessage<number>;
		/** Maximum number of files allowed. */
		max?: ValidationValueMessage<number>;
	};
	/** List of allowed file extensions. @default ALLOWED_FILE_EXTENSIONS_LIST */
	allowedExtensions?: string[];
	/** Whether these files are additional attachments (affects filtering and display). @default false */
	isAdditionalFiles?: boolean;
}

const connectionTimeout = 600000;

interface WaitingFile {
	resolve: (value: string | PromiseLike<string>) => void;
}

export const UploadFileSection = ({
	text = 'fileUpload:upload-files-text',
	title = 'fileUpload:upload-files-title',
	fileUploadStateHandlers: handlers,
	isError = false,
	isValidationOnUpload = false,
	validate,
	validation = {},
	isAdditionalFiles = false,
	allowedExtensions = ALLOWED_FILE_EXTENSIONS_LIST,
	sx = [],
}: IUploadFileSectionProps): React.JSX.Element => {
	const { t } = useTranslation<Namespace>();
	const { api } = useProductCenterApi();

	const dispatch = useAppDispatch();
	const { enqueueSnackbar } = useSnackbar();
	const stateUser = useAppSelector(selectUser);
	const filesAlreadyUploaded = useAppSelector((state) =>
		selectFileUploaded(handlers.fileStateSelector(state))
	).filter(({ isAdditional }) => isAdditional === isAdditionalFiles);
	const attachmentFolderName = useAppSelector((state) =>
		selectAttachmentFolderName(handlers.fileStateSelector(state))
	);
	const waitingFiles = useRef<WaitingFile[]>([]);

	const allowedExtensionsRegex = useMemo(
		() => convertFormatArrayToExtensionsValidationRegexp(allowedExtensions),
		[allowedExtensions]
	);

	const getValidationMessage = (
		validator: (value: number) => boolean,
		rule?: ValidationValueMessage<number>
	) =>
		rule && typeof rule.value === 'number' && validator(rule.value)
			? rule.message
			: undefined;

	const handleValidation = (newFiles: File[] = []) => {
		const minValidationMessage = getValidationMessage(
			(value: number) =>
				filesAlreadyUploaded.length + newFiles.length < value,
			validation.min
		);
		const maxValidationMessage = getValidationMessage(
			(value: number) =>
				filesAlreadyUploaded.length + newFiles.length > value,
			validation.max
		);

		if (minValidationMessage || maxValidationMessage) {
			enqueueSnackbar(
				t(
					(minValidationMessage ||
						maxValidationMessage) as ParseKeys<Namespace>
				),
				{
					variant: 'error',
				}
			);
			return false;
		}

		// if all files has been uploaded, then valid
		if (selectAllFiles.length === 0) {
			return true;
		}

		// check for failed
		if (selectAllFiles.some((file) => file.failReason)) {
			enqueueSnackbar(t('fileUpload:error-file-uploading-failed'), {
				variant: 'error',
			});
		}

		// check for any uploading files
		if (
			selectAllFiles.some(
				(file) =>
					!file.failReason &&
					file.percentCompleted > 0 &&
					file.percentCompleted < 100
			)
		) {
			enqueueSnackbar(t('fileUpload:error-file-being-uploaded'), {
				variant: 'error',
			});
		}

		return false;
	};

	const {
		filteredList,
		selectAllFiles,
		handleNewFiles,
		handleRetryUpload,
		handleProgressUpdate,
		handleDeleteFile,
		handleWarningDialogClose,
		uploadingFilenames,
	} = useUploadController({
		allowedExtensions: allowedExtensionsRegex,
		uploadFile: async (file) => {
			let folderName = attachmentFolderName;

			// check if it's nth file in a batch - first must upload before other to get attachment folder
			if (!folderName && uploadingFilenames[0] !== file.name) {
				folderName = await new Promise<string>((resolve) => {
					waitingFiles.current.push({
						resolve,
					});
				});

				if (!folderName) {
					throw new Error(
						'First file has not been uploaded. Attachment folder has not been created to upload the rest files of the batch'
					);
				}
			}

			return api.uploadTemporaryFile(
				{ file },
				{ attachmentsFolder: folderName },
				{
					onUploadProgress: ({ loaded, total }) => {
						if (!total) {
							return;
						}

						const percentCompleted = Math.round(
							(loaded * 100) / total
						);
						handleProgressUpdate(file, percentCompleted);
					},
					timeout: connectionTimeout,
				}
			);
		},
		fileUploaded: (file, response) => {
			const responseTyped = response;
			if (responseTyped) {
				if (
					!attachmentFolderName &&
					responseTyped.data.attachmentsFolder
				) {
					dispatch(
						handlers.attachmentFolderCreated(
							responseTyped.data.attachmentsFolder
						)
					);
				}

				const serverName = last(
					responseTyped.data.currentlyUploadedFiles?.filter((e) =>
						e.includes(file.name)
					)
				);

				if (serverName) {
					dispatch(
						handlers.fileUploaded(
							mapFileToFileData(
								file,
								serverName,
								mapToUploaderName(stateUser),
								isAdditionalFiles
							)
						)
					);

					handleDeleteFile(file);
				}
			}
		},
		fileExistsAlready: (file) =>
			!isValidFileUploadDuplicates(filesAlreadyUploaded, file),
	});

	validate(handleValidation);

	useEffect(() => {
		const failedUploadingFileIndex = selectAllFiles.findIndex(
			({ state }) => state === 'failed'
		);

		// if there are awaiting files, then release them in case of having:
		// - an attachment upload folder - that means first file has been uploaded correctly
		// - first file hasn't been uploaded - there is an error
		if (
			waitingFiles.current.length &&
			(attachmentFolderName || !failedUploadingFileIndex)
		) {
			waitingFiles.current.forEach(({ resolve }) => {
				resolve(attachmentFolderName);
			});
			waitingFiles.current = [];
		}
	}, [attachmentFolderName, selectAllFiles]);

	const handleDeleteUploadedFile = useCallback(
		(file: FileData) => {
			dispatch(handlers.fileDeleted({ serverName: file.serverName }));
			URL.revokeObjectURL(file.fileUrl);
		},
		[dispatch, handlers]
	);

	const allowedExtensionsString = useMemo(
		() => convertFormatArrayToFileExtensionsString(allowedExtensions),
		[allowedExtensions]
	);

	return (
		<>
			{filteredList && (
				<FileWarningDialog
					extensions={allowedExtensionsString}
					filteredFiles={filteredList}
					onClose={handleWarningDialogClose}
				/>
			)}
			<Box
				component={Paper}
				variant="flatGrey"
				sx={[
					{
						display: 'grid',

						gridTemplateColumns: {
							xs: 'repeat(1, 1fr)',
							sm: 'repeat(2, 1fr)',
						},

						gap: 2,
						p: 2,
					},
					...(Array.isArray(sx) ? sx : [sx]),
					{
						border: '1px solid',
						borderColor: isError ? 'error.main' : 'transparent',
					},
				]}>
				<Box>
					<Typography
						variant="h3"
						sx={{
							pb: 1,
						}}>
						{t(title)}
						{validation.min && (
							<Typography
								component="span"
								sx={{
									color: 'error.main',
								}}>
								{' *'}
							</Typography>
						)}
					</Typography>
					<Typography variant="body2">{t(text)}</Typography>
				</Box>
				<FileDragBox
					idPrefix={isAdditionalFiles ? 'additional-' : ''}
					filesAccepted={allowedExtensionsString}
					onNewFilesUploaded={(newFiles) => {
						if (
							!isValidationOnUpload ||
							handleValidation(newFiles)
						) {
							handleNewFiles({
								newFiles,
								externalFileCount: filesAlreadyUploaded.length,
							});
						}
					}}
				/>
				<Box
					sx={{
						margin: filesAlreadyUploaded.length
							? (theme) => ({
									xs: theme.spacing(2, 0, 0, 0),
								})
							: {},

						gridColumn: { sm: 'span 2' },
						'&>*': { marginTop: 2 },
					}}>
					{selectAllFiles.map((fileData) => (
						<ClientFileDisplay
							key={fileData.file.name}
							name={fileData.file.name}
							type={fileData.file.type}
							size={fileData.file.size}
							completed={false}
							percentCompleted={fileData.percentCompleted}
							failed={fileData.state === 'failed'}
							onDeleteClick={() =>
								handleDeleteFile(fileData.file)
							}
							onRetryClick={() =>
								handleRetryUpload(fileData.file)
							}
						/>
					))}
					{filesAlreadyUploaded.map((fileData) => (
						<ClientFileDisplay
							key={fileData.name}
							name={fileData.name}
							type={fileData.type}
							size={fileData.size}
							url={fileData.fileUrl}
							completed
							onDeleteClick={() =>
								handleDeleteUploadedFile(fileData)
							}
						/>
					))}
				</Box>
			</Box>
		</>
	);
};
